import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DesktopLayout from '../layouts/DesktopLayout';
import serviceApi from '../services/serviceApi';
import { REQUEST_STATUS_LABELS } from '../config/serviceConstants';
import { formatDate } from '../utils/dateFormatter';
import '../styles/ServiceClients.css';

const STATUS_FILTERS = [
  { value: 'upcoming', label: 'قادمة (7 أيام)' },
  { value: 'all',      label: 'الكل' },
  { value: 'pending',  label: 'قيد الانتظار' },
  { value: 'scheduled',label: 'مجدول' },
  { value: 'in-progress', label: 'قيد التنفيذ' },
  { value: 'completed',label: 'مكتمل' },
  { value: 'cancelled',label: 'ملغي' },
];

const STATUS_BADGE_CLASS = {
  pending:      'svc-req-badge--pending',
  scheduled:    'svc-req-badge--scheduled',
  'in-progress':'svc-req-badge--inprogress',
  completed:    'svc-req-badge--completed',
  cancelled:    'svc-req-badge--cancelled',
};

const ServiceRequestsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [requests, setRequests]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [activeNavItem, setActiveNavItem] = useState('services');

  const statusFilter = searchParams.get('status') || 'upcoming';

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        setError(null);
        const filters =
          statusFilter === 'all'      ? {} :
          statusFilter === 'upcoming' ? { upcoming: true } :
          { status: statusFilter };
        const res = await serviceApi.getAllRequests(filters);
        setRequests(res.data?.requests || []);
      } catch (err) {
        console.error('Error fetching requests:', err);
        setError('تعذر تحميل الطلبات');
      } finally {
        setLoading(false);
      }
    };
    fetchRequests();
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle="طلبات التنظيف"
    >
      <div className="svc-clients">

        {/* Header */}
        <div className="svc-clients__header">
          <h3 className="svc-clients__title">طلبات التنظيف</h3>
          <button
            className="svc-action svc-action--primary"
            onClick={() => navigate('/services/requests/new')}
          >
            <i className="bi bi-plus-circle" />
            طلب جديد
          </button>
        </div>

        {/* Status filter tabs */}
        <div className="svc-filter-tabs">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.value}
              className={`svc-filter-tab${statusFilter === f.value ? ' svc-filter-tab--active' : ''}`}
              onClick={() => setSearchParams({ status: f.value })}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">جاري التحميل...</span>
            </div>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="svc-clients__empty">
            <div className="svc-clients__empty-icon"><i className="bi bi-exclamation-triangle" /></div>
            <p className="svc-clients__empty-text">{error}</p>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && requests.length === 0 && (
          <div className="svc-clients__empty">
            <div className="svc-clients__empty-icon"><i className="bi bi-calendar-x" /></div>
            <p className="svc-clients__empty-text">لا توجد طلبات</p>
          </div>
        )}

        {/* Table */}
        {!loading && !error && requests.length > 0 && (
          <div className="svc-clients__table-wrap">
            <table className="svc-clients__table">
              <thead>
                <tr>
                  <th>التاريخ</th>
                  <th>العميل</th>
                  <th>الموقع</th>
                  <th>الوقت</th>
                  <th>الحالة</th>
                  <th>المهمة</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => (
                  <tr
                    key={req._id}
                    onClick={() => navigate(`/services/clients/${req.clientId?._id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={{ fontWeight: 500 }}>
                      {req.requestedDate ? formatDate(req.requestedDate) : '—'}
                    </td>
                    <td>
                      <div className="svc-client-name">
                        {req.clientId?.userId?.name || '—'}
                      </div>
                      {req.clientId?.userId?.clientCode && (
                        <div className="svc-client-code">
                          {req.clientId.userId.clientCode}
                        </div>
                      )}
                    </td>
                    <td>
                      <div style={{ fontSize: '0.9rem' }}>
                        {req.locationId?.name || '—'}
                      </div>
                      {req.locationId?.locationCode && (
                        <div className="svc-client-code">
                          {req.locationId.locationCode}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                      {req.requestedTimeSlot || '—'}
                    </td>
                    <td>
                      <span className={`svc-req-badge ${STATUS_BADGE_CLASS[req.status] || ''}`}>
                        {REQUEST_STATUS_LABELS[req.status] || req.status}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.82rem', color: '#6c757d' }}>
                      <span>{req.taskId?.ticketId || '—'}</span>
                      {req.invoiceId && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/services/invoices/${req.invoiceId}`);
                          }}
                          title="عرض الفاتورة"
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#471396',
                            cursor: 'pointer',
                            padding: '0 6px',
                            fontSize: '0.92rem',
                            verticalAlign: 'middle'
                          }}
                        >
                          <i className="bi bi-receipt" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </DesktopLayout>
  );
};

export default ServiceRequestsPage;
